import { useState, useEffect, useRef, useMemo } from "react";
import { Card, CardContent } from "@design-lab/shared/ui/card";
import { ColorSelect } from "@design-lab/shared/ui/ColorSelect";
import {
  useCurrentComposition,
  useCompositionsActions,
  useCompositionsList
} from "@design-lab/entities/composition";
import { useDesignProjectSync } from "@design-lab/features/design-project";
import { useProductColors } from "@design-lab/entities/product";
import { Loader2 } from "lucide-react";
import { TabCard } from "@design-lab/shared/ui/layouts/TabCard";
import { type Color } from "@design-lab/shared";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useExperiments } from "@design-lab/shared/lib/config";
import { shouldReorderDesignLabTabs } from "@design-lab/shared/lib/experiments";
import { AddColorModal } from "./AddColorModal";

export function SelectProductColor() {
  const { isDesktop } = useResponsive();
  const { isRestoring } = useDesignProjectSync();
  const experiments = useExperiments();
  const showSubTabs = useMemo(
    () => shouldReorderDesignLabTabs(experiments?.abDesignLabReorderTabs),
    [experiments?.abDesignLabReorderTabs]
  );

  const title = showSubTabs ? "3. Products & Colors" : "2. Products & Colors";

  const { currentComposition } = useCurrentComposition();
  const {
    addComposition,
    selectCurrentComposition,
    replaceComposition,
    removeComposition,
    previewComposition,
    clearCompositionPreview
  } = useCompositionsActions();
  const { formCompositions } = useCompositionsList();

  const {
    colors,
    selectedColor,
    currentProduct,
    addedCompositionIds,
    maxVisibleColors,
    isShowAllAvailable,
    recommendedColors
  } = useProductColors({
    maxColors: isDesktop ? 24 : 14
  });

  const [showAllColors, setShowAllColors] = useState(false);
  const [showAllRecommended, setShowAllRecommended] = useState(false);
  const [isAddColorModalOpen, setIsAddColorModalOpen] = useState(false);
  const [isInternalColorSelection, setIsInternalColorSelection] = useState(false);
  const previousSelectedColorIdRef = useRef(selectedColor?.id);

  // Auto expand color palette sections when color is selected from Add color modal & pick color
  useEffect(() => {
    const shouldExpand = (colorsList: Color[]) => {
      const index = colorsList.findIndex(color => color.id === currentSelectedColorId);
      return index >= 0 && colorsList.length > maxVisibleColors && index >= maxVisibleColors;
    };

    const currentSelectedColorId = selectedColor?.id;
    const previousSelectedColorId = previousSelectedColorIdRef.current;

    previousSelectedColorIdRef.current = currentSelectedColorId;

    const isExternalColorChange =
      currentSelectedColorId &&
      previousSelectedColorId !== undefined &&
      currentSelectedColorId !== previousSelectedColorId &&
      !isInternalColorSelection;

    if (isInternalColorSelection) {
      setIsInternalColorSelection(false);
    }

    if (!isExternalColorChange) return;

    const shouldExpandRecommended = shouldExpand(recommendedColors);
    const shouldExpandOtherColors = shouldExpand(colors);

    if (shouldExpandRecommended) setShowAllRecommended(true);

    if (shouldExpandOtherColors) setShowAllColors(true);
  }, [selectedColor?.id, isInternalColorSelection, recommendedColors, colors, maxVisibleColors]);

  if (isRestoring) {
    return (
      <Card className="flex h-full w-full flex-col overflow-hidden border-none lg:max-h-full lg:shadow-none">
        <CardContent className="flex h-full flex-1 flex-col space-y-4 overflow-y-auto p-2 lg:p-6">
          <Loader2 size={20} className="mx-auto animate-spin" />
          <p className="text-center text-sm font-semibold">Loading product colors</p>
        </CardContent>
      </Card>
    );
  }

  const handleColorSelect = (colorId: string) => {
    setIsInternalColorSelection(true);
    const color = [...recommendedColors, ...colors].find(c => c.id === colorId);
    if (!color || !currentProduct) return;

    const newComposition = currentProduct.compositions.find(comp => comp.colorName === color.name);
    if (newComposition && !addedCompositionIds.has(newComposition.id)) {
      addComposition({
        catalogId: currentProduct.catalogNumber,
        colorName: newComposition.colorName,
        compositionId: newComposition.id
      });
      selectCurrentComposition(newComposition.id);
      setIsAddColorModalOpen(false);
    }
    clearCompositionPreview(); // Clear override when selecting
  };

  const handleReplaceColor = (colorId: string) => {
    setIsInternalColorSelection(true);
    const color = [...recommendedColors, ...colors].find(c => c.id === colorId);
    if (!color) return;

    const newComposition = currentProduct?.compositions.find(comp => comp.colorName === color.name);
    if (newComposition && currentComposition) {
      replaceComposition(newComposition.id);
    }
    clearCompositionPreview(); // Clear override when selecting
  };

  const handleColorHover = (colorId: string) => {
    const color = [...recommendedColors, ...colors].find(c => c.id === colorId);
    if (!color) return;

    const newComposition = currentProduct?.compositions.find(comp => comp.colorName === color.name);
    if (newComposition) {
      previewComposition(newComposition.id);
    }
  };

  const handleColorBlur = () => {
    clearCompositionPreview();
  };

  const handleAddColor = () => {
    setIsAddColorModalOpen(true);
  };

  const handleRemoveComposition = () => {
    if (currentComposition && formCompositions.length > 1) {
      removeComposition(currentComposition.id);
    }
  };

  return (
    <TabCard showAddProductButton title={title} showNextButton={!isDesktop}>
      <ColorSelect
        selectedColor={selectedColor}
        recommendedColors={recommendedColors}
        allColors={colors}
        showToggleButton={isShowAllAvailable}
        onColorSelect={handleReplaceColor}
        onColorHover={handleColorHover}
        onColorBlur={handleColorBlur}
        showAllColors={showAllColors}
        onToggleShowAll={(show: boolean) => {
          setIsInternalColorSelection(true);
          setShowAllColors(show);
        }}
        showRecommendedToggle={recommendedColors.length > maxVisibleColors}
        showAllRecommended={showAllRecommended}
        onToggleShowAllRecommended={(show: boolean) => {
          setIsInternalColorSelection(true);
          setShowAllRecommended(show);
        }}
        maxColors={maxVisibleColors}
        product={currentProduct}
        onAddColor={handleAddColor}
        onRemoveComposition={handleRemoveComposition}
        showRemoveButton={formCompositions.length > 1}
      />
      <AddColorModal
        isOpen={isAddColorModalOpen}
        onClose={() => setIsAddColorModalOpen(false)}
        recommendedColors={recommendedColors}
        otherColors={colors}
        currentProduct={currentProduct}
        selectedColor={selectedColor}
        onColorSelect={handleColorSelect}
        maxColors={maxVisibleColors}
      />
    </TabCard>
  );
}
